import { Lesson, UserProfile, Relationship } from '../types';
import { signInWithPopup, GoogleAuthProvider, signOut } from 'firebase/auth';
import { auth, googleProvider } from '../firebase';
import { collection, query, where, getDocs, addDoc, updateDoc, doc, Timestamp, writeBatch, increment, getDoc } from 'firebase/firestore';
import { db } from '../firebase';

const CALENDAR_API_BASE = 'https://www.googleapis.com/calendar/v3';
const DRIVE_API_BASE = 'https://www.googleapis.com/drive/v3';

export class GoogleAuthError extends Error {
  constructor(public status: number) {
    super('Google Authentication Error');
    this.name = 'GoogleAuthError';
  }
}

export function clearGoogleSession() {
  sessionStorage.removeItem('google_access_token');
}

async function handleUnauthorized() {
  clearGoogleSession();
}

async function getAccessToken() {
  return sessionStorage.getItem('google_access_token');
}

export async function reconnectGoogle(forceSelect = false) {
  try {
    if (forceSelect) {
      googleProvider.setCustomParameters({ 
        prompt: 'select_account',
        access_type: 'offline'
      });
    } else {
      googleProvider.setCustomParameters({});
    }
    
    const result = await signInWithPopup(auth, googleProvider);
    const credential = GoogleAuthProvider.credentialFromResult(result);
    if (credential?.accessToken) {
      sessionStorage.setItem('google_access_token', credential.accessToken);
      return credential.accessToken;
    }
    return null;
  } catch (e) {
    console.error('Google Reconnection Failed', e);
    return null;
  }
}

export async function fetchUserCalendars() {
  const token = await getAccessToken();
  if (!token) return [];

  try {
    const response = await fetch(`${CALENDAR_API_BASE}/users/me/calendarList`, {
      headers: { 'Authorization': `Bearer ${token}` }
    });
    
    if (response.status === 401) {
      await handleUnauthorized();
      throw new GoogleAuthError(401);
    }

    if (!response.ok) return [];
    const data = await response.json();
    return data.items || [];
  } catch (e) {
    if (e instanceof GoogleAuthError) throw e;
    console.error('Failed to fetch calendars', e);
    return [];
  }
}

/**
 * Pulls events from primary Google Calendar and creates/updates lessons in App
 */
export async function importGoogleCalendarEvents(profile: UserProfile): Promise<number> {
  const token = await getAccessToken();
  if (!token || !profile.googleCalendarId) return 0;

  try {
    // 1. Get active students to match emails and prepare relationship lookup for credit handling
    const relSnap = await getDocs(query(
      collection(db, 'relationships'), 
      where('teacherId', '==', profile.uid),
      where('status', '==', 'active')
    ));
    
    const studentLookup: Record<string, string> = {};
    const relDataLookup: Record<string, { id: string, lessonsPaid: number, lessonsCompleted: number }> = {}; // studentId -> rel data

    await Promise.all(relSnap.docs.map(async d => {
      const data = d.data() as Relationship;
      let email = '';
      if (data.studentId.startsWith('pending:')) {
        email = data.studentId.split(':')[1];
      } else {
        const uDoc = await getDocs(query(collection(db, 'users'), where('uid', '==', data.studentId)));
        if (!uDoc.empty) email = uDoc.docs[0].data().email;
      }
      if (email) {
        studentLookup[email.toLowerCase()] = data.studentId;
        relDataLookup[data.studentId] = { 
          id: d.id!, 
          lessonsPaid: data.lessonsPaid, 
          lessonsCompleted: data.lessonsCompleted 
        };
      }
    }));

    // 2. Fetch Google Events
    const timeMin = new Date().toISOString();
    const encodedCalId = encodeURIComponent(profile.googleCalendarId);
    const response = await fetch(
      `${CALENDAR_API_BASE}/calendars/${encodedCalId}/events?timeMin=${timeMin}&singleEvents=true&orderBy=startTime`,
      { headers: { 'Authorization': `Bearer ${token}` } }
    );

    if (response.status === 401) {
      await handleUnauthorized();
      throw new GoogleAuthError(401);
    }

    if (!response.ok) return 0;
    const { items: googleEvents } = await response.json();
    if (!googleEvents || googleEvents.length === 0) return 0;

    // 3. Fetch existing lessons with googleEventId
    const existingLessonsSnap = await getDocs(query(
      collection(db, 'lessons'),
      where('teacherId', '==', profile.uid),
      where('googleEventId', '!=', null)
    ));
    const existingLessonsMap: Record<string, any> = {};
    existingLessonsSnap.docs.forEach(d => {
      const data = d.data();
      existingLessonsMap[data.googleEventId] = { id: d.id, ...data };
    });

    let syncCount = 0;
    const batch = writeBatch(db);

    for (const gEvent of googleEvents) {
      const attendees = gEvent.attendees || [];
      const matchEmail = attendees.find((a: any) => studentLookup[a.email?.toLowerCase()]);
      
      if (!matchEmail) continue;

      const studentId = studentLookup[matchEmail.email.toLowerCase()];
      const start = Timestamp.fromDate(new Date(gEvent.start.dateTime || gEvent.start.date));
      const end = Timestamp.fromDate(new Date(gEvent.end.dateTime || gEvent.end.date));
      const homeworkDesc = gEvent.description || '';
      const meetLink = gEvent.hangoutLink || null;
      
      // Determine incoming status based on [DONE] prefix
      const summary = gEvent.summary || '';
      const isIncomingDone = summary.startsWith('[DONE]');
      const incomingStatus = isIncomingDone ? 'completed' : 'scheduled';
      // Clean title if it has the prefix
      const cleanTitle = isIncomingDone ? summary.replace('[DONE]', '').trim() : summary;

      const lessonData: Partial<Lesson> = {
        teacherId: profile.uid,
        studentId,
        start,
        end,
        status: incomingStatus,
        type: 'lesson',
        title: cleanTitle || 'Music Lesson',
        googleEventId: gEvent.id,
        meetLink,
        source: 'google_calendar',
        homework: {
          description: homeworkDesc,
          attachments: existingLessonsMap[gEvent.id]?.homework?.attachments || []
        }
      };

      const rel = relDataLookup[studentId];

      if (existingLessonsMap[gEvent.id]) {
        const existing = existingLessonsMap[gEvent.id];
        
        // PROTECTION: If already completed in the app, don't update in the app
        if (existing.status === 'completed') continue;

        const hasTimeChanged = existing.start.toMillis() !== start.toMillis() || existing.end.toMillis() !== end.toMillis();
        const hasTitleChanged = existing.title !== lessonData.title;
        const hasStatusChanged = existing.status !== incomingStatus;
        
        if (hasTimeChanged || hasTitleChanged || hasStatusChanged) {
          batch.update(doc(db, 'lessons', existing.id), lessonData);
          
          // If syncing as completed, handle credit decrement and logging
          if (incomingStatus === 'completed' && rel) {
            batch.update(doc(db, 'relationships', rel.id), { 
              lessonsCompleted: increment(1) 
            });
            const updateRef = doc(collection(db, 'credit_updates'));
            batch.set(updateRef, {
              teacherId: profile.uid,
              studentId,
              timestamp: Timestamp.now(),
              change: -1,
              balanceAfter: (rel.lessonsPaid - rel.lessonsCompleted) - 1,
              type: 'sync',
              note: `Sync: ${cleanTitle}`
            });
            // Update local tracking to prevent balance drift in same loop
            rel.lessonsCompleted += 1;
          }
          
          syncCount++;
        }
      } else {
        // Create new
        const newDocRef = doc(collection(db, 'lessons'));
        batch.set(newDocRef, lessonData);
        
        // If created as completed, handle credit decrement and logging
        if (incomingStatus === 'completed' && rel) {
          batch.update(doc(db, 'relationships', rel.id), { 
            lessonsCompleted: increment(1) 
          });
          const updateRef = doc(collection(db, 'credit_updates'));
          batch.set(updateRef, {
            teacherId: profile.uid,
            studentId,
            timestamp: Timestamp.now(),
            change: -1,
            balanceAfter: (rel.lessonsPaid - rel.lessonsCompleted) - 1,
            type: 'sync',
            note: `Sync: ${cleanTitle}`
          });
          // Update local tracking
          rel.lessonsCompleted += 1;
        }
        
        syncCount++;
      }
    }

    await batch.commit();
    return syncCount;
  } catch (e) {
    console.error('Import failed', e);
    return 0;
  }
}

/**
 * Grants read-only access to a student for a specific Google Drive file.
 */
export async function grantDrivePermission(fileId: string, email: string) {
  const token = await getAccessToken();
  if (!token || !email) return;

  try {
    const response = await fetch(`${DRIVE_API_BASE}/files/${fileId}/permissions?sendNotificationEmail=false`, {
      method: 'POST',
      headers: {
        'Authorization': `Bearer ${token}`,
        'Content-Type': 'application/json'
      },
      body: JSON.stringify({
        role: 'reader',
        type: 'user',
        emailAddress: email
      })
    });

    if (response.status === 401) {
      await handleUnauthorized();
      throw new GoogleAuthError(401);
    }

    if (!response.ok) {
      const error = await response.text();
      console.warn(`Could not grant permission for file ${fileId} to ${email}:`, error);
    }
  } catch (e) {
    console.error('Drive permission API error', e);
  }
}

/**
 * Revokes access for a student from a specific Google Drive file.
 */
export async function revokeDrivePermission(fileId: string, email: string) {
  const token = await getAccessToken();
  if (!token || !email) return;

  try {
    // 1. Find the permission ID for this email
    const listResponse = await fetch(`${DRIVE_API_BASE}/files/${fileId}/permissions?fields=permissions(id,emailAddress)`, {
      headers: { 'Authorization': `Bearer ${token}` }
    });

    if (listResponse.status === 401) {
      await handleUnauthorized();
      throw new GoogleAuthError(401);
    }

    if (!listResponse.ok) return;
    const { permissions } = await listResponse.json();
    const targetPermission = permissions?.find((p: any) => p.emailAddress?.toLowerCase() === email.toLowerCase());

    if (targetPermission?.id) {
      // 2. Delete the specific permission
      await fetch(`${DRIVE_API_BASE}/files/${fileId}/permissions/${targetPermission.id}`, {
        method: 'DELETE',
        headers: { 'Authorization': `Bearer ${token}` }
      });
    }
  } catch (e) {
    console.error('Revoke Drive permission error', e);
  }
}

export async function syncLessonToGoogle(
  lesson: Lesson, 
  profile: UserProfile, 
  student?: Relationship
): Promise<{ id: string | null; meetLink?: string }> {
  const token = await getAccessToken();
  if (!token || !profile.googleCalendarId) return { id: null };

  const isBlock = lesson.type === 'block';
  const statusPrefix = lesson.status === 'completed' ? '[DONE] ' : lesson.status === 'canceled' ? '[CANCELED] ' : '';
  const title = isBlock 
    ? `${statusPrefix}${lesson.title || 'Unavailable'}` 
    : `${statusPrefix}${lesson.title || `Lesson: ${student?.studentName || 'Student'}`}`;

  // Reminders / Notifications
  const notificationEnabled = profile.notificationEnabled ?? true;
  const notificationMinutes = profile.notificationMinutes ?? 10;
  
  const reminders = notificationEnabled 
    ? { 
        useDefault: false, 
        overrides: [{ method: 'popup', minutes: notificationMinutes }] 
      }
    : { useDefault: false };

  const event: any = {
    summary: title,
    description: lesson.homework?.description || '',
    start: { dateTime: lesson.start.toDate().toISOString() },
    end: { dateTime: lesson.end.toDate().toISOString() },
    reminders
  };

  // Ensure the student is invited for non-block, active lessons
  if (!isBlock && student?.studentEmail) {
    event.attendees = [
      { email: student.studentEmail }
    ];
  }

  // Handle Google Meet conferencing
  const meetPreference = lesson.createMeet ?? (profile.googleMeetEnabled ?? true);
  
  if (!isBlock && lesson.status === 'scheduled' && meetPreference) {
    if (!lesson.meetLink) {
      event.conferenceData = {
        createRequest: {
          requestId: lesson.id || Math.random().toString(36).substring(7),
          conferenceSolutionKey: { type: 'hangoutsMeet' },
        },
      };
    }
  }

  const hasEventId = !!lesson.googleEventId && lesson.googleEventId.trim().length > 0;
  const encodedCalendarId = encodeURIComponent(profile.googleCalendarId);
  
  const sendUpdates = (lesson.status === 'completed') ? 'none' : 'all';
  
  const url = hasEventId 
    ? `${CALENDAR_API_BASE}/calendars/${encodedCalendarId}/events/${lesson.googleEventId}?conferenceDataVersion=1&sendUpdates=${sendUpdates}`
    : `${CALENDAR_API_BASE}/calendars/${encodedCalendarId}/events?conferenceDataVersion=1&sendUpdates=${sendUpdates}`;

  const method = hasEventId ? 'PATCH' : 'POST';

  try {
    const response = await fetch(url, {
      method,
      headers: {
        'Authorization': `Bearer ${token}`,
        'Content-Type': 'application/json'
      },
      body: JSON.stringify(event)
    });

    if (response.status === 401) {
      await handleUnauthorized();
      throw new GoogleAuthError(401);
    }

    if (!response.ok) {
      const errorText = await response.text();
      console.error(`Google Calendar ${method} error:`, errorText);
      if (response.status === 404 && hasEventId) {
        return { id: 'RECREATE_REQUESTED' };
      }
      return { id: null };
    }

    const result = await response.json();
    return { 
      id: result.id, 
      meetLink: result.hangoutLink || undefined 
    };
  } catch (e) {
    if (e instanceof GoogleAuthError) throw e;
    console.error('Calendar sync network error', e);
    return { id: null };
  }
}

export async function deleteGoogleEvent(calendarId: string, eventId: string) {
  const token = await getAccessToken();
  if (!token || !calendarId || !eventId) return;

  try {
    const encodedCalendarId = encodeURIComponent(calendarId);
    const response = await fetch(`${CALENDAR_API_BASE}/calendars/${encodedCalendarId}/events/${eventId}?sendUpdates=all`, {
      method: 'DELETE',
      headers: { 'Authorization': `Bearer ${token}` }
    });

    if (response.status === 401) {
      await handleUnauthorized();
      throw new GoogleAuthError(401);
    }
  } catch (e) {
    if (e instanceof GoogleAuthError) throw e;
    console.error('Failed to delete Google event', e);
  }
}